import { useRef, useState } from "react";
import { motion, useScroll, useSpring, useTransform, useMotionValueEvent } from "framer-motion";
import { Section, Eyebrow, Heading, P } from "../Chapter";

const ERAS = [
    { era: "Letter by sea", year: "1850", label: "~30 days", seconds: 2592000 },
    { era: "Airmail", year: "1930", label: "~5 days", seconds: 432000 },
    { era: "Telegram", year: "1900", label: "~4 hours", seconds: 14400 },
    { era: "Email", year: "1990", label: "~1 minute", seconds: 60 },
    { era: "Message", year: "today", label: "instant", seconds: 0.4 },
];
const MAXLOG = Math.log10(2592001);

const fmt = (s) => {
    if (s < 1) return "instant";
    if (s >= 86400) { const d = s / 86400; return `${d < 10 ? d.toFixed(1) : Math.round(d)} days`; }
    if (s >= 3600) return `${Math.round(s / 3600)} hours`;
    if (s >= 60) return `${Math.round(s / 60)} min`;
    return `${Math.round(s)} sec`;
};

const secondsAt = (v) => {
    const n = ERAS.length - 1;
    const pos = Math.min(n, Math.max(0, v * n));
    const i = Math.min(n - 1, Math.floor(pos));
    const t = pos - i;
    const a = Math.log10(Math.max(ERAS[i].seconds, 0.001));
    const b = Math.log10(Math.max(ERAS[i + 1].seconds, 0.001));
    return Math.pow(10, a + (b - a) * t);
};

export const Collapse = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });
    const p = useSpring(scrollYProgress, { stiffness: 80, damping: 26, mass: 0.4 });

    const scaleX = useTransform(p, (v) => Math.max(0.004, Math.log10(secondsAt(v) + 1) / MAXLOG));
    const [time, setTime] = useState(fmt(ERAS[0].seconds));
    const [idx, setIdx] = useState(0);

    useMotionValueEvent(p, "change", (v) => {
        setTime(fmt(secondsAt(v)));
        setIdx(Math.round(v * (ERAS.length - 1)));
    });

    return (
        <div ref={ref} className="relative h-[360vh] bg-surface">
            <div className="sticky top-0 flex h-[100svh] items-center overflow-hidden px-6 md:px-10">
                <div className="mx-auto grid w-full max-w-6xl grid-cols-1 items-center gap-14 lg:grid-cols-[1fr_1.1fr]">
                    <div>
                        <Eyebrow>IV. The collapse</Eyebrow>
                        <Heading>And then it got fast</Heading>
                        <div className="mx-auto max-w-[52ch] space-y-6">
                            <p className="font-serif text-[1.12rem] leading-[1.75] text-ink/90 md:text-[1.2rem]">
                                The telegraph cut months into hours. The telephone cut hours into the
                                length of a conversation. In 1971 a programmer named Ray Tomlinson sent
                                the first network email between two machines standing side by side, and
                                reached for the <span className="text-wax">@</span> key to separate a
                                person from the place they were. He later said he couldn't remember what
                                the message said. It didn't matter. The point was that it arrived.
                            </p>
                            <p className="font-serif text-[1.12rem] leading-[1.75] text-ink/90 md:text-[1.2rem]">
                                Then the web. Then a computer in every pocket. Then a message that arrives
                                before you've finished deciding whether to send it.
                            </p>
                        </div>
                    </div>

                    <div>
                        <div className="flex items-end justify-between">
                            <div>
                                <div className="font-sans text-[0.68rem] tracking-[0.3em] uppercase text-sepia">
                                    delivery time
                                </div>
                                <div className="mt-2 font-serif text-6xl leading-none tabular-nums text-ink md:text-8xl">
                                    {time}
                                </div>
                            </div>
                            <div className="text-right">
                                <div className="font-serif text-2xl text-wax md:text-3xl">{ERAS[idx].era}</div>
                                <div className="font-sans text-xs tracking-[0.25em] uppercase text-sepia">{ERAS[idx].year}</div>
                            </div>
                        </div>

                        <div className="mt-6 h-[6px] w-full overflow-hidden rounded-full bg-rule/60">
                            <motion.div style={{ scaleX }} className="h-full w-full origin-left rounded-full bg-wax" />
                        </div>

                        <div className="mt-10 divide-y divide-rule border-y border-rule">
                            {ERAS.map((e, i) => (
                                <div
                                    key={e.era}
                                    className="flex items-center justify-between py-3 transition-opacity duration-300"
                                    style={{ opacity: i === idx ? 1 : 0.4 }}
                                >
                                    <span className="font-serif text-lg text-ink">
                                        {e.era}
                                        <span className="text-sepia">, {e.year}</span>
                                    </span>
                                    <span className={`font-sans text-sm tracking-wide ${i === idx ? "text-wax" : "text-sepia"}`}>
                                        {e.label}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export const CollapseOutro = () => (
    <Section className="pt-0">
        <P>
            In under two centuries, the distance between saying something and being heard went to
            zero. Nothing else in human communication has ever changed that much, that quickly.
        </P>
    </Section>
);
